import React, { useState } from 'react';
import { PlusIcon, SearchIcon, FilterIcon, EyeIcon, CheckCircleIcon, AlertTriangleIcon, XCircleIcon, BuildingIcon, MapPinIcon, GlobeIcon, ChevronLeftIcon, ChevronRightIcon, BarChartIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { VendorCard } from './VendorCard';
export function VendorList({
  vendors,
  onViewVendor,
  setCurrentView,
  locale
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [complianceFilter, setComplianceFilter] = useState('all');
  const [esgFilter, setESGFilter] = useState('all');
  const [viewMode, setViewMode] = useState('table'); // Default to table view
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Tenant settings simulation
  const [isESGEnabled] = useState(true); // In a real app, this would come from tenant settings
  const [hasESGViewPermission] = useState(true); // In a real app, this would come from user permissions
  const [showESGColumn, setShowESGColumn] = useState(true); // User preference for showing the column
  // Filter vendors based on search term and filters
  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) || vendor.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || vendor.status === statusFilter;
    const matchesCompliance = complianceFilter === 'all' || vendor.complianceStatus === complianceFilter;
    // ESG filter logic
    let matchesESG = true;
    if (isESGEnabled && esgFilter !== 'all') {
      if (esgFilter === 'not-provided') {
        matchesESG = !vendor.esg || !vendor.esg.score;
      } else if (vendor.esg && vendor.esg.score !== undefined) {
        if (esgFilter === 'excellent') matchesESG = vendor.esg.score >= 80;else if (esgFilter === 'good') matchesESG = vendor.esg.score >= 60 && vendor.esg.score < 80;else if (esgFilter === 'moderate') matchesESG = vendor.esg.score >= 40 && vendor.esg.score < 60;else if (esgFilter === 'needs-review') matchesESG = vendor.esg.score < 40;
      } else {
        matchesESG = false;
      }
    }
    return matchesSearch && matchesStatus && matchesCompliance && matchesESG;
  });
  // Calculate pagination
  const totalVendors = filteredVendors.length;
  const totalPages = Math.ceil(totalVendors / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalVendors);
  const currentVendors = filteredVendors.slice(startIndex, endIndex);
  // Get availability display text
  const getAvailabilityText = vendor => {
    if (vendor.isGlobal) {
      return 'All Entities';
    }
    if (vendor.entities && vendor.entities.length > 0) {
      return vendor.sites && vendor.sites.length > 0 ? `${vendor.entities.length} Entities, ${vendor.sites.length} Sites` : `${vendor.entities.length} Entities`;
    }
    return 'Unknown';
  };
  // Get availability icon
  const getAvailabilityIcon = vendor => {
    if (vendor.isGlobal) {
      return <GlobeIcon className="h-4 w-4 text-blue-500" data-id="element-19595" />;
    } else if (vendor.sites && vendor.sites.length > 0) {
      return <MapPinIcon className="h-4 w-4 text-amber-500" data-id="element-19596" />;
    } else {
      return <BuildingIcon className="h-4 w-4 text-indigo-500" data-id="element-19597" />;
    }
  };
  // Get ESG status badge
  const getESGStatusBadge = vendor => {
    if (!vendor.esg || vendor.esg.score === undefined) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-19598">
          Not Provided
        </span>;
    }
    const score = vendor.esg.score;
    if (score >= 80) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-19599">
          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19600" />
          Excellent
        </span>;
    } else if (score >= 60) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-19601">
          <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-19602" />
          Good
        </span>;
    } else if (score >= 40) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-19603">
          <AlertTriangleIcon className="mr-1 h-3 w-3" data-id="element-19604" />
          Moderate
        </span>;
    } else {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-19605">
          <XCircleIcon className="mr-1 h-3 w-3" data-id="element-19606" />
          Needs Review
        </span>;
    }
  };
  // Handle page changes
  const goToPage = page => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };
  // Handle page size changes
  const handlePageSizeChange = e => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };
  return <div data-id="element-19607">
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0" data-id="element-19608">
        <div className="flex-1 max-w-md" data-id="element-19609">
          <div className="relative rounded-md shadow-sm" data-id="element-19610">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-19611">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-19612" />
            </div>
            <input type="text" value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setCurrentPage(1); // Reset to first page when searching
          }} className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search vendors..." data-id="element-19613" />
          </div>
        </div>
        <div className="flex space-x-2" data-id="element-19614">
          <div data-id="element-19615">
            <select value={statusFilter} onChange={e => {
            setStatusFilter(e.target.value);
            setCurrentPage(1); // Reset to first page when filtering
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19616">
              <option value="all" data-id="element-19617">All Statuses</option>
              <option value="Active" data-id="element-19618">Active</option>
              <option value="Pending Onboarding" data-id="element-19619">Pending Onboarding</option>
              <option value="Inactive" data-id="element-19620">Inactive</option>
              <option value="Invited" data-id="element-19621">Invited</option>
            </select>
          </div>
          <div data-id="element-19622">
            <select value={complianceFilter} onChange={e => {
            setComplianceFilter(e.target.value);
            setCurrentPage(1); // Reset to first page when filtering
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19623">
              <option value="all" data-id="element-19624">All Compliance</option>
              <option value="Compliant" data-id="element-19625">Compliant</option>
              <option value="Pending Review" data-id="element-19626">Pending Review</option>
              <option value="Incomplete" data-id="element-19627">Incomplete</option>
              <option value="Non-Compliant" data-id="element-19628">Non-Compliant</option>
              <option value="Not Started" data-id="element-19629">Not Started</option>
            </select>
          </div>
          {/* ESG Filter - Only show if ESG is enabled */}
          {isESGEnabled && hasESGViewPermission && <div data-id="element-19630">
              <select value={esgFilter} onChange={e => {
            setESGFilter(e.target.value);
            setCurrentPage(1); // Reset to first page when filtering
          }} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19631">
                <option value="all" data-id="element-19632">All ESG Status</option>
                <option value="excellent" data-id="element-19633">Excellent (80+)</option>
                <option value="good" data-id="element-19634">Good (60-79)</option>
                <option value="moderate" data-id="element-19635">Moderate (40-59)</option>
                <option value="needs-review" data-id="element-19636">Needs Review (&lt;40)</option>
                <option value="not-provided" data-id="element-19637">Not Provided</option>
              </select>
            </div>}
          <div className="inline-flex shadow-sm rounded-md" data-id="element-19638">
            <button onClick={() => setViewMode('table')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-l-md ${viewMode === 'table' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-19639">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-19640">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" data-id="element-19641" />
              </svg>
            </button>
            <button onClick={() => setViewMode('grid')} className={`inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-r-md ${viewMode === 'grid' ? 'bg-indigo-100 text-indigo-800' : 'bg-white text-gray-700 hover:bg-gray-50'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-19642">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-19643">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" data-id="element-19644" />
              </svg>
            </button>
          </div>
          <button onClick={() => setCurrentView('onboard-vendor')} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19645">
            <PlusIcon className="h-4 w-4 mr-2" data-id="element-19646" />
            Add Vendor
          </button>
        </div>
      </div>

      {/* Column visibility settings */}
      {isESGEnabled && hasESGViewPermission && <div className="mb-4 flex items-center" data-id="element-19647">
          <label className="flex items-center text-sm text-gray-700" data-id="element-19648">
            <input type="checkbox" className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" checked={showESGColumn} onChange={e => setShowESGColumn(e.target.checked)} data-id="element-19649" />
            <span className="ml-2" data-id="element-19650">Show ESG Score column</span>
          </label>
        </div>}

      {/* Pagination info */}
      <div className="flex justify-between items-center mb-4" data-id="element-19651">
        <div className="text-sm text-gray-700" data-id="element-19652">
          Showing <span className="font-medium" data-id="element-19653">{startIndex + 1}</span> to{' '}
          <span className="font-medium" data-id="element-19654">{endIndex}</span> of{' '}
          <span className="font-medium" data-id="element-19655">{totalVendors}</span> vendors
        </div>
        <div className="flex items-center space-x-2" data-id="element-19656">
          <span className="text-sm text-gray-700" data-id="element-19657">Rows per page:</span>
          <select value={pageSize} onChange={handlePageSizeChange} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-19658">
            <option value={10} data-id="element-19659">10</option>
            <option value={25} data-id="element-19660">25</option>
            <option value={50} data-id="element-19661">50</option>
            <option value={100} data-id="element-19662">100</option>
          </select>
        </div>
      </div>

      {/* Table View */}
      {viewMode === 'table' && <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-19663">
          {currentVendors.length === 0 ? <div className="px-4 py-8 text-center text-gray-500" data-id="element-19664">
              No vendors found matching your search criteria.
            </div> : <div className="overflow-x-auto" data-id="element-19665">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-19666">
                <thead className="bg-gray-50" data-id="element-19667">
                  <tr data-id="element-19668">
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19669">
                      Vendor
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19670">
                      Type
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19671">
                      Availability
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19672">
                      Country
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19673">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19674">
                      Compliance
                    </th>
                    {/* ESG Score Column - Only show if enabled */}
                    {isESGEnabled && hasESGViewPermission && showESGColumn && <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19675">
                        ESG Score
                      </th>}
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-19676">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-19677">
                  {currentVendors.map(vendor => <tr key={vendor.id} className="hover:bg-gray-50" data-id="element-19678">
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-19679">
                        <div className="text-sm font-medium text-indigo-600" data-id="element-19680">
                          {vendor.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-19681">{vendor.id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-19682">
                        {vendor.type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-19683">
                        <div className="flex items-center text-sm text-gray-500" data-id="element-19684">
                          {getAvailabilityIcon(vendor)}
                          <span className="ml-1" data-id="element-19685">
                            {getAvailabilityText(vendor)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-19686">
                        {vendor.country}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-19687">
                        <StatusBadge status={vendor.status.toLowerCase()} text={vendor.status} data-id="element-19688" />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-19689">
                        <div className="flex items-center" data-id="element-19690">
                          {vendor.complianceStatus === 'Compliant' && <CheckCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-green-500" data-id="element-19691" />}
                          {vendor.complianceStatus === 'Pending Review' && <AlertTriangleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-yellow-500" data-id="element-19692" />}
                          {(vendor.complianceStatus === 'Incomplete' || vendor.complianceStatus === 'Not Started') && <AlertTriangleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" data-id="element-19693" />}
                          {vendor.complianceStatus === 'Non-Compliant' && <XCircleIcon className="flex-shrink-0 mr-1.5 h-4 w-4 text-red-500" data-id="element-19694" />}
                          <span className="text-sm text-gray-500" data-id="element-19695">
                            {vendor.complianceStatus}
                          </span>
                        </div>
                      </td>
                      {/* ESG Score Column Content - Only show if enabled */}
                      {isESGEnabled && hasESGViewPermission && showESGColumn && <td className="px-6 py-4 whitespace-nowrap" data-id="element-19696">
                            {vendor.esg ? <div className="flex items-center" data-id="element-19697">
                                <span className="text-sm font-medium mr-2" data-id="element-19698">
                                  {vendor.esg.score !== undefined ? vendor.esg.score : 'N/A'}
                                </span>
                                {vendor.esg.score !== undefined && getESGStatusBadge(vendor)}
                              </div> : <span className="text-sm text-gray-500" data-id="element-19699">N/A</span>}
                          </td>}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-19700">
                        <button onClick={() => onViewVendor(vendor)} className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19701">
                          <EyeIcon className="h-4 w-4 mr-1" data-id="element-19702" />
                          View Details
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>}

      {/* Grid View */}
      {viewMode === 'grid' && <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-19703">
          {currentVendors.length === 0 ? <div className="col-span-full text-center py-12 bg-white rounded-lg shadow" data-id="element-19704">
              <div className="mx-auto h-12 w-12 text-gray-400 flex items-center justify-center" data-id="element-19705">
                <BuildingIcon className="h-12 w-12" data-id="element-19706" />
              </div>
              <h3 className="mt-2 text-sm font-medium text-gray-900" data-id="element-19707">
                No vendors found
              </h3>
              <p className="mt-1 text-sm text-gray-500" data-id="element-19708">
                Try adjusting your search or filters.
              </p>
              <div className="mt-6" data-id="element-19709">
                <button onClick={() => setCurrentView('onboard-vendor')} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-19710">
                  <PlusIcon className="h-4 w-4 mr-2" data-id="element-19711" />
                  Add Vendor
                </button>
              </div>
            </div> : currentVendors.map(vendor => <VendorCard key={vendor.id} vendor={vendor} onViewVendor={onViewVendor} getAvailabilityIcon={getAvailabilityIcon} getAvailabilityText={getAvailabilityText} locale={locale} isESGEnabled={isESGEnabled && hasESGViewPermission && showESGColumn} data-id="element-19712" />)}
        </div>}

      {/* Pagination controls */}
      {totalVendors > 0 && <div className="mt-4 flex items-center justify-between" data-id="element-19713">
          <div className="flex-1 flex justify-between sm:hidden" data-id="element-19714">
            <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-19715">
              Previous
            </button>
            <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-19716">
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-19717">
            <div data-id="element-19718">
              <p className="text-sm text-gray-700" data-id="element-19719">
                Page <span className="font-medium" data-id="element-19720">{currentPage}</span> of{' '}
                <span className="font-medium" data-id="element-19721">{totalPages}</span>
              </p>
            </div>
            <div data-id="element-19722">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-19723">
                <button onClick={() => goToPage(1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-19724">
                  <span className="sr-only" data-id="element-19725">First Page</span>
                  <div className="h-5 w-5" aria-hidden="true" data-id="element-19726" />
                </button>
                <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-19727">
                  <span className="sr-only" data-id="element-19728">Previous</span>
                  <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-19729" />
                </button>
                {/* Page numbers */}
                {Array.from({
              length: Math.min(5, totalPages)
            }, (_, i) => {
              // Calculate page numbers to show
              let pageNum;
              if (totalPages <= 5) {
                // If 5 or fewer pages, show all
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                // If near the start
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                // If near the end
                pageNum = totalPages - 4 + i;
              } else {
                // If in the middle
                pageNum = currentPage - 2 + i;
              }
              return <button key={pageNum} onClick={() => goToPage(pageNum)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum ? 'bg-indigo-50 border-indigo-500 text-indigo-600 z-10' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-19730">
                        {pageNum}
                      </button>;
            })}
                <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-19731">
                  <span className="sr-only" data-id="element-19732">Next</span>
                  <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-19733" />
                </button>
                <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-19734">
                  <span className="sr-only" data-id="element-19735">Last Page</span>
                  <div className="h-5 w-5" aria-hidden="true" data-id="element-19736" />
                </button>
              </nav>
            </div>
          </div>
        </div>}
    </div>;
}